import React, { useState, useEffect } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { BiCheckSquare } from 'react-icons/bi';
import { TiWarningOutline } from 'react-icons/ti';
import { CgPassword } from 'react-icons/cg';
import { FaRegThumbsUp } from 'react-icons/fa';
import passwordValidator from 'password-validator';

const ChangePasswordModal = (props) => {
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [schema, setSchema] = useState(null);

  const [passwordValid, setPasswordValid] = useState(false);
  const [hasMin, setHasMin] = useState(false);
  const [hasUpper, setHasUpper] = useState(false);
  const [hasLower, setHasLower] = useState(false);
  const [hasDigits, setHasDigits] = useState(false);
  const [hasSymbols, setHasSymbols] = useState(false);
  const [confirmMatch, setConfirmMatch] = useState(false);

  useEffect(() => {
    const schema = new passwordValidator();

    schema
      .is().min(8)
      .is().max(100)
      .has().uppercase()
      .has().lowercase()
      .has().digits()
      .has().symbols();
    
    setSchema(schema);
  }, []);

  useEffect(() => {
    if (schema) {
      let validationArr = schema.validate(newPass, { list: true });
      let validationMap = validationArr.reduce((map, item) => {
        map[item] = item;
        return map;
      }, {});

      validationMap['min'] ? setHasMin(false) : setHasMin(true);
      validationMap['uppercase'] ? setHasUpper(false) : setHasUpper(true);
      validationMap['lowercase'] ? setHasLower(false) : setHasLower(true);
      validationMap['digits'] ? setHasDigits(false) : setHasDigits(true);
      validationMap['symbols'] ? setHasSymbols(false) : setHasSymbols(true);
    }
  }, [newPass]);

  useEffect(() => {
    // Recompute on BOTH fields: editing the new password after confirming it must clear the
    // "Passwords match!" state, not leave it stale-green.
    setConfirmMatch(newPass !== "" && confirmPass === newPass);
  }, [confirmPass, newPass]);

  useEffect(() => {
    if (
      hasMin &&
      hasUpper &&
      hasLower &&
      hasDigits &&
      hasSymbols
    ) {
      setPasswordValid(true);
    } else {
      setPasswordValid(false);
    }
  }, [hasMin, hasUpper, hasLower, hasDigits, hasSymbols]);

  const cleanup = () => {
    setPasswordValid(false);
    setHasMin(false);
    setHasUpper(false);
    setHasLower(false);
    setHasDigits(false);
    setHasSymbols(false);
    setConfirmMatch(false);
  }

  const handleClose = () => {
    cleanup();
    props.setShow(false)
  };

  return (
    <Modal 
      dialogClassName="change-password-modal"
      className="p-5 change-password-modal" 
      show={props.show} 
      onHide={handleClose}
    >
      <Modal.Body className="pt-3">
        <Row className="pt-3 pb-3 pl-4 pr-4">
          <Col>
            <h4 className="pb-3">
              <CgPassword className="change-pass-header-icon mr-2" />
              Change Password
            </h4>
          </Col>
        </Row>
        <Row className="pl-4 pr-4">
          <Col lg="4">
            <div className="pass-req">
              <div className="pass-req-header">
                Passwords must have:
              </div>
              { !hasMin ? (
                <div className="invalid-pass-req">
                  <TiWarningOutline /> 
                  At least 8 characters
                </div>
              ) : (
                <div className="valid-pass-req">
                  <BiCheckSquare /> 
                  At least 8 characters
                </div>
              )}
              { !hasUpper ? (
                <div className="invalid-pass-req">
                  <TiWarningOutline /> 
                  1 uppercase letter
                </div>
              ) : (
                <div className="valid-pass-req">
                  <BiCheckSquare /> 
                  1 uppercase letter
                </div>
              )}
              { !hasLower ? (
                <div className="invalid-pass-req">
                  <TiWarningOutline /> 
                  1 lowercase letter
                </div>
              ) : (
                <div className="valid-pass-req">
                  <BiCheckSquare /> 
                  1 lowercase letter
                </div>
              )}
              { !hasDigits ? (
                <div className="invalid-pass-req">
                  <TiWarningOutline /> 
                  1 number
                </div>
              ) : (
                <div className="valid-pass-req">
                  <BiCheckSquare /> 
                  1 number
                </div>
              )}
              { !hasSymbols ? (
                <div className="invalid-pass-req">
                  <TiWarningOutline /> 
                  1 special character
                </div>
              ) : (
                <div className="valid-pass-req">
                  <BiCheckSquare /> 
                  1 special character
                </div>
              )}
            </div>
          </Col>
          <Col>
            <Form>
              <Form.Group>
                <Form.Label>Old Password</Form.Label>
                <Form.Control
                  data-testid="change-pw-old"
                  type="password"
                  onChange={(e) => setOldPass(e.target.value)}
                />
                <Form.Text>
                  <small>Enter your current password.</small>
                </Form.Text>
              </Form.Group>
              <Form.Group className="position-relative">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  data-testid="change-pw-new"
                  className={passwordValid && 'validated-input'}
                  type="password"
                  onChange={(e) => setNewPass(e.target.value)}
                />
                { passwordValid && <FaRegThumbsUp className="validated-input-icon" />}
                <Form.Text>
                  { !passwordValid ? (
                    <small>Enter a new valid password.</small>
                  ) : (
                    <small className='validated-input-helper'>Valid password!</small>
                  )}
                </Form.Text>
              </Form.Group>
              <Form.Group className="position-relative">
                <Form.Label>Confirm</Form.Label>
                <Form.Control
                  data-testid="change-pw-confirm"
                  className={confirmMatch && 'validated-input'}
                  type="password"
                  onChange={(e) => setConfirmPass(e.target.value)}
                />
                { confirmMatch && <FaRegThumbsUp className="validated-input-icon" />}
                <Form.Text>
                  { !confirmMatch ? (
                    <small>Enter a new valid password.</small>
                  ) : (
                    <small className='validated-input-helper'>Passwords match!</small>
                  )}
                </Form.Text>
              </Form.Group>
            </Form>
            <Row>
              <Col>
                <div className='d-flex justify-content-end'>
                  <div>
                    <Button
                      variant="outline-secondary"
                      className="w-100 pt-1 pb-1 pr-3 pl-3"
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </div>
                  <div className="ml-2">
                    <Button
                      disabled={!(oldPass && passwordValid && confirmMatch)}
                      onClick={async () => {
                        await props.changePasswordHandler(oldPass, newPass, confirmPass);
                        handleClose();
                      }}
                      variant="primary"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default ChangePasswordModal;